import { asMoneyWithoutZeros, formatNumberToKNotation } from 'helpers';
import useProjectDetails from '../useProjectDetails';
import { toDecimalPoint } from '@round/utils';
import { platformLabels, tooltipCopy } from 'Modules/Plans/helpers';
import HeadlineStats, { Stats } from 'Modules/Plans/components/HeadlineStats/HeadlineStats';
import { creatorbase } from '@round/api';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { useAggregatedPostStats } from '@round/creatorbase-helpers/api-hooks';
import styles from './PlatformHeadlineStats.module.css';
import cn from 'classnames';

type Props = {
    platform: creatorbase.Platform;
};

const AggregatedPlatformStats = ({ platform }: Props) => {
    const { getCurrencyById } = useCurrencies();
    const { project } = useProjectDetails();

    const enabled = !!project?.platforms.includes(platform) && typeof project?.id === 'number';
    const { data, isLoading, isFetching } = useAggregatedPostStats(
        typeof project?.id === 'number' ? { project_id: project.id, platform } : undefined,
        { enabled }
    );
    const isRefreshing = isFetching && !isLoading;

    //there should only be one currency in the data, so we can just take the first entry
    const cpmCurrencyValuePair = Object.entries(data?.cpm || {})[0] || [];
    const cpm = cpmCurrencyValuePair.length
        ? asMoneyWithoutZeros(cpmCurrencyValuePair[1], getCurrencyById(Number(cpmCurrencyValuePair[0])))
        : '-';

    const totalCostCurrencyValuePair = Object.entries(data?.total_budget || {})[0] || [];
    const totalCost = totalCostCurrencyValuePair.length
        ? asMoneyWithoutZeros(totalCostCurrencyValuePair[1], getCurrencyById(Number(totalCostCurrencyValuePair[0])))
        : '-';

    const displayValues: Stats = [
        {
            label: 'Total Posts',
            value: data?.post_count,
        },
        {
            label: 'Total Followers',
            value: typeof data?.follower_count === 'number' ? formatNumberToKNotation(data?.follower_count) : '-',
        },
        {
            label: 'Total Views',
            value: typeof data?.view_count === 'number' ? formatNumberToKNotation(data?.view_count) : '-',
        },
        {
            label: 'Engagement Rate',
            value:
                typeof data?.engagement_rate === 'number' ? `${toDecimalPoint(data?.engagement_rate * 100, 2)}%` : '-',
            tooltip: tooltipCopy.engagementRate,
        },
        {
            label: `${platformLabels[platform]} Cost`,
            value: totalCost,
        },
        {
            label: 'CPM',
            value: cpm,
            tooltip: tooltipCopy.cpm,
        },
    ];

    return (
        <div className={cn({ [styles.refreshing]: isRefreshing })}>
            <HeadlineStats stats={displayValues} isLoading={isLoading} />
        </div>
    );
};

export default AggregatedPlatformStats;
